import { collection, query, orderBy, getDocs, where, addDoc, updateDoc, doc, serverTimestamp, getDoc, deleteDoc } from 'firebase/firestore'
import { db } from '../firebase'
import CryptoJS from 'crypto-js'

// Worker passwords - SHA256 hashes
const WORKER_PASSWORDS = {
  'Tugce': '8c8140276c13d2acac050bdc1079b0d8a32bcba792b2f0302ba6bbb48e0f5bed', // "tugce4356"
  'Sule': 'fc7e1d39d58542739c24a7a3f7e9da95eef56d6b22217f21075c435bb12bf50a'   // "sule8912"
}

// Admin password - SHA256 hash
const ADMIN_PASSWORD_HASH = '681332a09e53b303b88dbec576760872f7b9bfd2f9cbc451b5355b2fe7094013' // "katon3532"

// Marketing Reply API Endpoint
const AI_ENDPOINT = 'https://us-central1-astroai-200db.cloudfunctions.net/marketingReply'

// Default system prompt template
export const DEFAULT_SYSTEM_PROMPT = `You are the social media voice of a Brand Ambassador for the app "Astro Way."
Since this is a single API call, you must understand the product deeply to generate the correct reply.

# PART 1: PRODUCT CONTEXT (What you are promoting)

- App Name: Astro Way
- Core Value: A hyper-personalized AI Astrologer. It's not just generic horoscopes; it uses NASA data to calculate precise planetary degrees, transits, and house cusps.
- Key Feature: Astro Chat (An AI chatbot that knows your specific chart and answers life questions instantly).
- Target Audience: Gen Z & Millennials who are tired of vague astrology and want specific, actionable, real talk answers.
- Your Role: You are NOT the founder. You are a Super User or Ambassador who loves the app because it solves the pain of manual calculation. You share it because it works.

# PART 2: ENGAGEMENT STRATEGY

Your goal is to drive profile visits by creating an Information Gap.

- Do NOT give the full astrological answer (e.g., You will get married in 2026).
- DO tell them why they can't know the answer yet (e.g., It depends on the exact degree of your 7th house ruler).
- The Hook: Make them realize they are missing a critical piece of data (Degree, Orb, House Cusp) that they can find via the link in your bio.

# PART 3: STRICT RULES

1. Platform Tone:
   - TikTok: Casual, slang-heavy (bestie, oof, lowkey, real tea, rn). High energy.
   - Instagram: Aesthetic, cool, slightly more mature/expert tone. No bestie overuse.
2. Formatting: Use mostly lowercase. NO quotation marks ("") for emphasis. NO robotic transitions (However, Moreover).
3. No Hard Selling: Never say Download the app. Instead say check the link in my bio, I use this tool, or my secret weapon.

# PART 4: REPLY ANGLES

ANGLE A: THE MYSTERIOUS EXPERT (Gatekeeping)
- Logic: Validate the user, then drop a technical truth bomb.
- Structure: That feeling is real... but to know for sure, you need to check the exact degree of your [Planet]. Without that, it's just guessing.

ANGLE B: THE TRANSPARENT AMBASSADOR (The Tool Plug)
- Logic: Be honest about using a tool.
- Structure: Honestly, manual calculation is a headache 💀 I just use Astro Way for this because it tracks the degrees automatically. I have the link in my bio if you want to save time.

ANGLE C: THE VIRAL STORYTELLER (The Pivot)
- Logic: Start with a specific, vivid, slightly dramatic/funny micro-story related to the topic. Hook them with entertainment first. Then, PIVOT quickly to the tool as the solution.
- Structure: [Dramatic/Funny Hook about a past failure/chaos] + [The realization] + [The Tool Mention].
- Example: Omg I literally cried in a starbucks last mercury retrograde because my ex texted... 😭 I was a mess until I checked my transits on Astro Way and realized it was just a 2-day pass. Check your dates before you panic text.

⚠️ CRITICAL: Always reply in the SAME LANGUAGE as the comment.

# OUTPUT FORMAT
You MUST respond in valid JSON format only. No extra text before or after the JSON.

{
  "answers": [
    { "option": "Mysterious Expert", "reply": "your gatekeeping truth bomb reply here" },
    { "option": "Transparent Ambassador", "reply": "your honest tool plug reply here" },
    { "option": "Viral Storyteller", "reply": "your dramatic/funny micro-story pivot reply here" }
  ]
}`

// Build full system message based on post context
export function buildSystemMessage(postDescription, platform, customPrompt = null) {
  const basePrompt = customPrompt || DEFAULT_SYSTEM_PROMPT
  return `${basePrompt}

PLATFORM: ${platform === 'tiktok' ? 'TikTok' : 'Instagram'}

POST CONTEXT:
${postDescription}`
}

class FirebaseAICommentService {
  constructor() {
    this.collectionName = 'ai_comment_threads'
  }

  verifyPassword(password) {
    const hash = CryptoJS.SHA256(password).toString()

    // Check admin password first
    if (hash === ADMIN_PASSWORD_HASH) {
      return { valid: true, worker: 'Admin', isAdmin: true }
    }

    // Check worker passwords
    for (const [worker, workerHash] of Object.entries(WORKER_PASSWORDS)) {
      if (hash === workerHash) {
        return { valid: true, worker, isAdmin: false }
      }
    }
    return { valid: false, worker: null, isAdmin: false }
  }

  async createThread(threadData) {
    try {
      const docRef = await addDoc(collection(db, this.collectionName), {
        postUrl: threadData.postUrl || '',
        platform: threadData.platform || 'tiktok',
        postDescription: threadData.postDescription || '',
        systemPrompt: threadData.systemPrompt || DEFAULT_SYSTEM_PROMPT,
        workerTag: threadData.workerTag || '',
        replies: [], // Array of { comment, generatedReply, createdAt }
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })
      return docRef.id
    } catch (error) {
      console.error('Error creating thread:', error)
      throw error
    }
  }

  async getAllThreads() {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('updatedAt', 'desc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (error) {
      console.error('Error fetching threads:', error)
      throw error
    }
  }

  async getThreadsByWorker(workerTag) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('workerTag', '==', workerTag),
        orderBy('updatedAt', 'desc')
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (error) {
      console.error('Error fetching threads by worker:', error)
      throw error
    }
  }

  async getThread(threadId) {
    try {
      const docRef = doc(db, this.collectionName, threadId)
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        }
      }
      return null
    } catch (error) {
      console.error('Error fetching thread:', error)
      throw error
    }
  }

  async updateThread(threadId, data) {
    try {
      const docRef = doc(db, this.collectionName, threadId)
      await updateDoc(docRef, {
        ...data,
        updatedAt: serverTimestamp()
      })
    } catch (error) {
      console.error('Error updating thread:', error)
      throw error
    }
  }

  async deleteThread(threadId) {
    try {
      const docRef = doc(db, this.collectionName, threadId)
      await deleteDoc(docRef)
    } catch (error) {
      console.error('Error deleting thread:', error)
      throw error
    }
  }

  async getWorkerStats() {
    try {
      const threads = await this.getAllThreads()
      const stats = {}

      threads.forEach(thread => {
        const worker = thread.workerTag || 'Unknown'
        if (!stats[worker]) {
          stats[worker] = {
            threadCount: 0,
            replyCount: 0,
            lastActivity: null
          }
        }
        stats[worker].threadCount++
        stats[worker].replyCount += (thread.replies || []).length

        const updatedAt = thread.updatedAt?.toDate?.() || thread.updatedAt
        if (!stats[worker].lastActivity || updatedAt > stats[worker].lastActivity) {
          stats[worker].lastActivity = updatedAt
        }
      })

      return stats
    } catch (error) {
      console.error('Error fetching worker stats:', error)
      throw error
    }
  }

  // Generate reply for a comment
  async generateReply(threadId, comment) {
    try {
      const thread = await this.getThread(threadId)
      if (!thread) throw new Error('Thread not found')

      // Build system message with post context and custom prompt
      const systemMessage = buildSystemMessage(thread.postDescription, thread.platform, thread.systemPrompt)

      // Prepare messages for AI
      const messages = [
        {
          role: 'system',
          content: systemMessage
        },
        {
          role: 'user',
          content: `Please generate a reply for this comment:\n\n"${comment}"`
        }
      ]

      // Call the Marketing Reply endpoint
      const response = await fetch(AI_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messages,
          model: 'gemini-2.5-flash'
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `AI request failed: ${response.statusText}`)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'AI request failed')
      }

      // Parse AI response into two options
      const answers = this.parseAIResponse(data.reply)

      // Save the reply to thread history
      const replies = thread.replies || []
      const newReply = {
        id: Date.now().toString(),
        comment: comment,
        answers: answers,
        createdAt: new Date().toISOString()
      }
      replies.unshift(newReply) // Add to beginning

      await this.updateThread(threadId, { replies })

      return newReply
    } catch (error) {
      console.error('Error generating reply:', error)
      throw error
    }
  }

  // Parse AI response (JSON format) into answers array
  parseAIResponse(text) {
    if (!text) return []

    try {
      // Try to extract JSON from the response
      let jsonStr = text.trim()

      // If response has extra text, try to find JSON object
      const jsonMatch = jsonStr.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        jsonStr = jsonMatch[0]
      }

      const parsed = JSON.parse(jsonStr)

      if (parsed.answers && Array.isArray(parsed.answers)) {
        return parsed.answers
      }

      return []
    } catch (e) {
      console.error('Failed to parse AI response as JSON:', e)
      // Fallback: return raw text as single answer
      return [{ option: 'Response', reply: text }]
    }
  }

  // Delete a reply from history
  async deleteReply(threadId, replyId) {
    try {
      const thread = await this.getThread(threadId)
      if (!thread) throw new Error('Thread not found')

      const replies = (thread.replies || []).filter(r => r.id !== replyId)
      await this.updateThread(threadId, { replies })
    } catch (error) {
      console.error('Error deleting reply:', error)
      throw error
    }
  }
}

export { FirebaseAICommentService }
